import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    CalendarDays,
    Truck,
    Wrench,
    Settings,
    LogOut,
    Bell,
    Search,
    ChevronLeft,
    ChevronRight,
    Menu,
    Moon,
    Sun
} from 'lucide-react';
import { useAuth } from '@features/auth/context/AuthContext';
import { useTheme } from '@shared/contexts/ThemeContext';
import { APP_VERSION, COMMIT_HASH } from '@/version';

interface SidebarLayoutProps {
    children: React.ReactNode;
}

export const SidebarLayout: React.FC<SidebarLayoutProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    // Navigation Items
    const navigation = [
        { name: 'Tableau de Bord', icon: LayoutDashboard, path: '/', requiredPerm: null },
        { name: 'Planning', icon: CalendarDays, path: '/planning', requiredPerm: 'planning' },
        { name: 'Flotte', icon: Truck, path: '/fleet', requiredPerm: 'fleet' },
        { name: 'Outillage', icon: Wrench, path: '/inventory', requiredPerm: 'tooling' },
        { name: 'Paramètres', icon: Settings, path: '/settings', requiredPerm: 'admin_only' },
    ];

    // Filter navigation based on user role AND granular permissions
    const filteredNav = navigation.filter(item => {
        if (!user) return false;

        // Admin sees everything
        if (user.role === 'admin') return true;

        // Dashboard is for everyone
        if (!item.requiredPerm) return true;

        // Admin-only routes
        if (item.requiredPerm === 'admin_only') return false;

        // For other roles, check specific permissions
        const perms = user.permissions || { 
            planning: user.role === 'planner' ? 'write' : 'none', 
            fleet: user.role === 'planner' ? 'write' : 'none', 
            tooling: user.role === 'planner' ? 'write' : 'none' 
        };

        if (item.requiredPerm === 'planning') return perms.planning !== 'none';
        if (item.requiredPerm === 'fleet') return perms.fleet !== 'none';
        if (item.requiredPerm === 'tooling') return perms.tooling !== 'none';

        return false;
    });

    const isActive = (path: string) => {
        if (path === '/' && location.pathname !== '/') return false;
        return location.pathname.startsWith(path);
    };

    return (
        <div className="min-h-screen bg-[var(--bg-color)] text-[var(--text-main)] transition-colors duration-300 flex">

            {/* Mobile Overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
          fixed md:sticky top-0 left-0 h-screen z-50
          bg-[var(--panel-bg)] border-r border-[var(--glass-border)]
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-20' : 'w-64'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          flex flex-col
        `}
            >
                {/* Logo Area */}
                <div className="h-16 flex items-center justify-between px-4 border-b border-[var(--glass-border)] relative">
                    {!collapsed && (
                        <div className="flex items-center gap-2 overflow-hidden">
                            <img src="/logo.png" alt="Vigie" className="w-8 h-8 rounded-lg shadow-lg shadow-primary/20 shrink-0" />
                            <div className="flex flex-col">
                                <span className="font-bold text-lg leading-tight truncate">Vigie</span>
                                <span className="text-[10px] text-muted truncate">L'Intelligence Opérationnelle</span>
                            </div>
                        </div>
                    )}
                    {collapsed && (
                        <div className="w-full flex justify-center">
                            <img src="/logo.png" alt="Vigie" className="w-8 h-8 rounded-lg shadow-lg shadow-primary/20" />
                        </div>
                    )}

                    {/* Toggle Button (Desktop) */}
                    <button
                        onClick={() => setCollapsed(!collapsed)}
                        className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-[var(--panel-bg)] border border-[var(--glass-border)] rounded-full flex items-center justify-center text-muted hover:text-primary transition-colors shadow-sm hidden md:flex"
                    >
                        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                    </button>
                </div>

                {/* User Profile Snippet */}
                <div className={`p-4 border-b border-[var(--glass-border)] ${collapsed ? 'items-center' : ''} flex flex-col gap-3 transition-all`}>
                    <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold shadow-inner shrink-0">
                            {user?.username?.charAt(0).toUpperCase()}
                        </div>
                        {!collapsed && (
                            <div className="flex flex-col overflow-hidden">
                                <span className="font-medium text-sm truncate">{user?.username}</span>
                                <span className="text-xs text-muted truncate capitalize">{user?.role}</span>
                            </div>
                        )}
                    </div>
                    {!collapsed && (
                        <div className="text-xs text-center px-2 py-1 rounded bg-primary/10 text-primary border border-primary/20">
                            {user?.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                        </div>
                    )}
                </div>

                {/* Navigation Links */}
                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 custom-scrollbar">
                    {filteredNav.map((item) => (
                        <button
                            key={item.path}
                            onClick={() => {
                                navigate(item.path);
                                setMobileOpen(false);
                            }}
                            className={`
                 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative
                 ${isActive(item.path)
                                    ? 'bg-primary text-white shadow-md shadow-primary/20'
                                    : 'text-muted hover:bg-[var(--glass-border)] hover:text-[var(--text-main)]'
                                }
                 ${collapsed ? 'justify-center' : ''}
               `}
                            title={collapsed ? item.name : ''}
                        >
                            <item.icon size={20} className={`shrink-0 ${isActive(item.path) ? 'animate-pulse-slow' : ''}`} />
                            {!collapsed && (
                                <span className="font-medium text-sm">{item.name}</span>
                            )}

                            {/* Tooltip for collapsed mode */}
                            {collapsed && (
                                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                                    {item.name}
                                </div>
                            )}
                        </button>
                    ))}
                </nav>

                {/* Notifications & Quick Stats (Optional in Sidebar) */}
                {!collapsed && (
                    <div className="px-4 py-4">
                        <div className="p-3 rounded-xl bg-[var(--glass-border)]/30 border border-[var(--glass-border)]">
                            <h4 className="text-xs font-semibold text-muted mb-2 uppercase tracking-wider">État Système</h4>
                            <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-[var(--text-main)]">Serveur</span>
                                <span className="text-emerald-400 flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                    Online
                                </span>
                            </div>
                            <div className="w-full bg-gray-700/30 rounded-full h-1 mt-2 overflow-hidden">
                                <div className="bg-primary h-full rounded-full" style={{ width: '100%' }}></div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer Actions */}
                <div className="p-3 border-t border-[var(--glass-border)] space-y-2">
                    <button
                        onClick={toggleTheme}
                        className={`
                 w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                 text-muted hover:text-[var(--text-main)] hover:bg-[var(--glass-border)]
                 ${collapsed ? 'justify-center' : ''}
              `}
                        title="Changer le thème"
                    >
                        {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
                        {!collapsed && <span className="text-sm">Mode {theme === 'light' ? 'Sombre' : 'Clair'}</span>}
                    </button>

                    <button
                        onClick={logout}
                        className={`
                 w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                 text-red-400 hover:bg-red-500/10 hover:text-red-500
                 ${collapsed ? 'justify-center' : ''}
              `}
                        title="Déconnexion"
                    >
                        <LogOut size={20} />
                        {!collapsed && <span className="text-sm">Déconnexion</span>}
                    </button>
                </div>

                {/* Version Info */}
                {!collapsed && (
                    <div className="pb-2 text-center">
                        <span className="text-[10px] text-muted/50">v{APP_VERSION}</span>
                    </div>
                )}
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden relative">
                {/* Mobile Header */}
                <header className="md:hidden h-16 border-b border-[var(--glass-border)] bg-[var(--panel-bg)] flex items-center justify-between px-4 shrink-0">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setMobileOpen(true)}
                            className="p-2 -ml-2 text-muted hover:text-[var(--text-main)]"
                        >
                            <Menu size={24} />
                        </button>
                        <span className="font-bold text-lg">Vigie</span>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-bold">
                        {user?.username?.charAt(0)}
                    </div>
                </header>

                {/* Top Bar (Desktop: Breadcrumbs/Actions) - Optional */}
                <div className="hidden md:flex h-16 items-center justify-between px-8 border-b border-[var(--glass-border)] bg-[var(--bg-color)]/50 backdrop-blur-sm shrink-0">
                    {/* Breadcrumb / Title */}
                    <div className="flex items-center gap-2 text-muted text-sm">
                        <LayoutDashboard size={16} />
                        <span>/</span>
                        <span className="font-medium text-[var(--text-main)] capitalize">
                            {location.pathname === '/' ? 'Tableau de Bord' : location.pathname.substring(1)}
                        </span>
                    </div>

                    {/* Right Actions */}
                    <div className="flex items-center gap-4">
                        <div className="relative group">
                            <Search size={18} className="text-muted absolute left-3 top-1/2 -translate-y-1/2 group-hover:text-primary transition-colors" />
                            <input
                                type="text"
                                placeholder="Recherche globale..."
                                className="pl-9 pr-4 py-1.5 bg-[var(--input-bg)] border border-[var(--glass-border)] rounded-full text-sm w-48 focus:w-64 transition-all focus:border-primary outline-none"
                            />
                        </div>

                        <button className="relative p-2 text-muted hover:text-[var(--text-main)] transition-colors">
                            <Bell size={20} />
                            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                        </button>
                    </div>
                </div>

                {/* Scrollable Page Content */}
                <div className="flex-1 overflow-y-auto p-4 md:p-8 hide-scrollbar-force relative">
                    {children}

                    {/* Footer */}
                    <div className="mt-8 pb-4 flex flex-col items-center justify-center gap-1 opacity-40 hover:opacity-100 transition-opacity">
                        <div className="flex items-center gap-2 text-[10px] font-medium text-muted">
                            <span>VIGIE © 2026</span>
                            <span className="w-0.5 h-0.5 rounded-full bg-current"></span>
                            <span className="font-mono">v{APP_VERSION} ({COMMIT_HASH})</span>
                        </div>
                        <a
                            href="https://ma-dsi.info"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[9px] uppercase tracking-wider text-muted hover:text-primary transition-colors"
                        >
                            Propulsé par <span className="font-bold">ma-dsi.info</span>
                        </a>
                    </div>
                </div>
            </main>

        </div>
    );
};
